import { type ReactNode, useEffect, useId, useRef } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  FileSearch,
  Info,
  LoaderCircle,
  LockKeyhole,
  Search,
  X,
} from 'lucide-react';
import type { Tone } from '../lib/types';

export const cx = (...values: Array<string | false | null | undefined>): string => values.filter(Boolean).join(' ');

const statusTone = (label: string): Tone => {
  const value = label.toLowerCase();
  if (['ready', 'accepted', 'complete', 'completed', 'approved', 'healthy', 'resolved', 'closed', 'success'].some((item) => value.includes(item))) return 'success';
  if (['block', 'reject', 'critical', 'quarantine', 'danger', 'open'].some((item) => value.includes(item))) return 'danger';
  if (['review', 'attention', 'medium', 'pending', 'draft', 'remediation', 'warning'].some((item) => value.includes(item))) return 'warning';
  if (['high'].some((item) => value === item)) return 'danger';
  if (['info', 'validate', 'prepare', 'submit', 'locked'].some((item) => value.includes(item))) return 'info';
  return 'neutral';
};

const toneIcon: Record<Tone, typeof Info> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  info: Info,
  neutral: Info,
};

export function StatusChip({ label, tone, compact = false }: { label: string; tone?: Tone; compact?: boolean }) {
  const resolved = tone ?? statusTone(label);
  const Icon = toneIcon[resolved];
  return (
    <span className={cx('status-chip', `status-${resolved}`, compact && 'status-compact')}>
      <Icon aria-hidden="true" size={compact ? 12 : 14} strokeWidth={2.25} />
      <span>{label}</span>
    </span>
  );
}

export function ProgressBar({ value, tone = 'info', label }: { value: number; tone?: Tone; label?: string }) {
  const safeValue = Math.min(100, Math.max(0, value));
  return (
    <div className="progress-wrap">
      {label ? (
        <div className="progress-label">
          <span>{label}</span><strong>{safeValue.toLocaleString('de-DE')} %</strong>
        </div>
      ) : null}
      <div className="progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={safeValue} aria-label={label ?? `${safeValue} Prozent`}>
        <span className={cx('progress-fill', `progress-${tone}`)} style={{ width: `${safeValue}%` }} />
      </div>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        <p className="page-description">{description}</p>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-header">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Button({
  children,
  variant = 'secondary',
  size = 'medium',
  icon,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'small' | 'medium';
  icon?: ReactNode;
}) {
  return (
    <button className={cx('button', `button-${variant}`, `button-${size}`, className)} {...props}>
      {icon}<span>{children}</span>
    </button>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Suchen …', label = 'Suchen' }: { value: string; onChange: (value: string) => void; placeholder?: string; label?: string }) {
  return (
    <label className="search-input">
      <span className="sr-only">{label}</span>
      <Search aria-hidden="true" size={17} />
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
      {value ? <button type="button" onClick={() => onChange('')} aria-label="Suche leeren"><X size={14} /></button> : null}
    </label>
  );
}

export function Select({ label, value, onChange, children }: { label: string; value: string; onChange: (value: string) => void; children: ReactNode }) {
  return (
    <label className="select-field">
      <span className="sr-only">{label}</span>
      <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>
      <ChevronDown aria-hidden="true" size={15} />
    </label>
  );
}

export function MetricDelta({ children, positive = true }: { children: ReactNode; positive?: boolean }) {
  return <span className={cx('metric-delta', positive ? 'delta-positive' : 'delta-neutral')}>{children}</span>;
}

export function EmptyState({
  title = 'Noch keine Daten',
  description = 'Für diese Auswahl wurden keine Einträge gefunden.',
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon"><FileSearch size={24} /></div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function LoadingState({ fullPage = false }: { fullPage?: boolean }) {
  return (
    <div className={cx('loading-state', fullPage && 'loading-full')} role="status" aria-live="polite">
      <LoaderCircle className="spin" size={24} />
      <span>Arbeitsbereich wird geladen …</span>
    </div>
  );
}

export function ErrorState({ title = 'Daten konnten nicht geladen werden', description, onRetry }: { title?: string; description?: string; onRetry?: () => void }) {
  return (
    <div className="state-panel" role="alert">
      <div className="state-icon state-danger"><AlertCircle size={24} /></div>
      <h2>{title}</h2>
      <p>{description ?? 'Prüfen Sie die Verbindung oder versuchen Sie es erneut.'}</p>
      {onRetry ? <Button variant="primary" onClick={onRetry}>Erneut versuchen</Button> : null}
    </div>
  );
}

export function PermissionState({ scope = 'diesen Bereich' }: { scope?: string }) {
  return (
    <div className="state-panel" role="alert">
      <div className="state-icon state-warning"><LockKeyhole size={24} /></div>
      <h2>Zugriff eingeschränkt</h2>
      <p>Ihre aktuelle Rolle besitzt keine Berechtigung für {scope}. Wenden Sie sich an einen Organisationsadministrator.</p>
      <a className="text-link" href="mailto:access@example.invalid">Zugriff anfragen <ArrowRight size={14} /></a>
    </div>
  );
}

function useDialogLifecycle(open: boolean, onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', handler);
    };
  }, [open]);
}

export function Drawer({ open, onClose, title, description, children, footer }: { open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode; footer?: ReactNode }) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  useDialogLifecycle(open, onClose);
  useEffect(() => { if (open) window.setTimeout(() => closeButton.current?.focus(), 0); }, [open]);
  if (!open) return null;
  return (
    <div className="overlay" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="dialog-header">
          <div><h2 id={titleId}>{title}</h2>{description ? <p>{description}</p> : null}</div>
          <button className="icon-button" onClick={onClose} ref={closeButton} aria-label="Detailansicht schließen"><X size={19} /></button>
        </header>
        <div className="drawer-content">{children}</div>
        {footer ? <footer className="dialog-footer">{footer}</footer> : null}
      </aside>
    </div>
  );
}

export function Modal({ open, onClose, title, description, children, footer, size = 'medium' }: { open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode; footer?: ReactNode; size?: 'small' | 'medium' | 'large' }) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  useDialogLifecycle(open, onClose);
  useEffect(() => { if (open) window.setTimeout(() => closeButton.current?.focus(), 0); }, [open]);
  if (!open) return null;
  return (
    <div className="overlay overlay-centered" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className={cx('modal', `modal-${size}`)} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="dialog-header">
          <div><h2 id={titleId}>{title}</h2>{description ? <p>{description}</p> : null}</div>
          <button className="icon-button" onClick={onClose} ref={closeButton} aria-label="Dialog schließen"><X size={19} /></button>
        </header>
        <div className="modal-content">{children}</div>
        {footer ? <footer className="dialog-footer">{footer}</footer> : null}
      </section>
    </div>
  );
}

export function DetailList({ items }: { items: Array<{ label: string; value: ReactNode; full?: boolean }> }) {
  return (
    <dl className="detail-list">
      {items.map((item) => (
        <div key={item.label} className={item.full ? 'detail-full' : undefined}>
          <dt>{item.label}</dt><dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Avatar({ name, size = 'medium' }: { name: string; size?: 'small' | 'medium' }) {
  const value = name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
  return <span className={cx('avatar', `avatar-${size}`)} aria-hidden="true">{value}</span>;
}

export function FormField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="form-field">
      <span className="form-label">{label}</span>
      {children}
      {hint ? <span className="form-hint">{hint}</span> : null}
    </label>
  );
}

export function Checkbox({ checked, onChange, label, description }: { checked: boolean; onChange: (checked: boolean) => void; label: string; description?: string }) {
  return (
    <label className="checkbox-field">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="checkbox-box"><CheckCircle2 size={13} /></span>
      <span><strong>{label}</strong>{description ? <small>{description}</small> : null}</span>
    </label>
  );
}
